/**
 * AutofillSettingsSection Component
 *
 * Settings of the post-extraction autofill — which missing fields are filled automatically
 * after a document is extracted. Every choice is stored the moment it changes.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { InvoiceAutofillRateSource } from '@insio/shared-types';
import { InputWrapper, Loader, Select, Toggle } from '@insio/ui-toolkit';
import { InvoiceAutofillSettingsDocument, UpdateInvoiceAutofillSettingsDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useCanChangeAutomationSettings } from './use-can-change-automation-settings';

// Derived from the enum the backend registers into the schema — a new member
// shows up here, and its missing translation key fails the i18n tests.
const RATE_SOURCE_OPTIONS = Object.values(InvoiceAutofillRateSource);

interface AutofillDraft {
  isLineAmountsEnabled: boolean;
  isAccountingTotalsEnabled: boolean;
  rateSource: InvoiceAutofillRateSource;
}

export function AutofillSettingsSection() {
  const t = useFeatureTranslation('systemSettings.automation.autofill');
  const { showError } = useToastHelpers();
  const canChange = useCanChangeAutomationSettings();

  const { data, loading, error } = useQuery(InvoiceAutofillSettingsDocument);
  // The response carries no id to normalise, so the settings query is refetched —
  // otherwise the local draft keeps comparing against the pre-save snapshot.
  const [updateSettings] = useMutation(UpdateInvoiceAutofillSettingsDocument, {
    refetchQueries: [{ query: InvoiceAutofillSettingsDocument }],
    awaitRefetchQueries: true
  });

  const [draft, setDraft] = useState<AutofillDraft>({
    isLineAmountsEnabled: true,
    isAccountingTotalsEnabled: true,
    rateSource: InvoiceAutofillRateSource.INVOICE_ONLY
  });

  const settings = data?.invoiceAutofillSettings;

  useEffect(() => {
    if (settings) {
      setDraft({
        isLineAmountsEnabled: settings.isLineAmountsEnabled,
        isAccountingTotalsEnabled: settings.isAccountingTotalsEnabled,
        rateSource: settings.rateSource
      });
    }
  }, [settings]);

  /** Stored the moment it changes; a refused write puts the choice back. */
  const handleChange = async (change: Partial<AutofillDraft>) => {
    if (!canChange) return;
    const next = { ...draft, ...change };
    setDraft(next);
    try {
      // errorPolicy 'none' (vs global 'all') makes GraphQL errors throw into the catch below
      await updateSettings({ variables: { input: next }, errorPolicy: 'none' });
    } catch {
      setDraft(draft);
      showError(t('saveError'));
    }
  };

  if (loading && !settings) {
    return <Loader.Section />;
  }

  if (error) {
    return <p className='text-sm text-error-text'>{t('loadError')}</p>;
  }

  return (
    <div className='space-y-lg'>
      <div>
        <h2 className='text-xl font-bold text-foreground-base'>{t('title')}</h2>
        <p className='text-sm text-foreground-muted mt-xs'>{t('description')}</p>
      </div>

      <div className='space-y-lg'>
        <InputWrapper
          label={t('lineAmounts.label')}
          hint={t('lineAmounts.description')}
          className='gap-xs'
        >
          <Toggle
            checked={draft.isLineAmountsEnabled}
            onChange={isLineAmountsEnabled => handleChange({ isLineAmountsEnabled })}
            disabled={!canChange}
            labelText={draft.isLineAmountsEnabled ? t('enabled.on') : t('enabled.off')}
            testId='autofill-line-amounts-toggle'
          />
        </InputWrapper>

        <div className='space-y-sm'>
          <InputWrapper
            label={t('accountingTotals.label')}
            hint={t('accountingTotals.description')}
            className='gap-xs'
          >
            <Toggle
              checked={draft.isAccountingTotalsEnabled}
              onChange={isAccountingTotalsEnabled => handleChange({ isAccountingTotalsEnabled })}
              disabled={!canChange}
              labelText={draft.isAccountingTotalsEnabled ? t('enabled.on') : t('enabled.off')}
              testId='autofill-accounting-totals-toggle'
            />
          </InputWrapper>

          {draft.isAccountingTotalsEnabled && (
            <div className='space-y-xs'>
              <InputWrapper label={t('rateSource.label')} className='gap-xs max-w-100'>
                <Select
                  value={draft.rateSource}
                  onChange={value =>
                    handleChange({ rateSource: value as InvoiceAutofillRateSource })
                  }
                  options={RATE_SOURCE_OPTIONS.map(option => ({
                    value: option,
                    label: t(`rateSource.options.${option}.label`)
                  }))}
                  disabled={!canChange}
                  testId='autofill-rate-source-select'
                />
              </InputWrapper>
              <p className='text-sm text-foreground-muted' data-testid='autofill-rate-source-hint'>
                {t(`rateSource.options.${draft.rateSource}.description`)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
